// src/components/auth/ResetPassword.tsx
import React, {useEffect, useState, useCallback} from 'react'
import {useParams, Link, useNavigate} from 'react-router-dom'
import {FiEye, FiEyeOff, FiLock} from 'react-icons/fi'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../constants/apiConstants'
import MetaTags from '../../../meta/MetaTags'
import {FaArrowLeft} from 'react-icons/fa'
import InvalidLinkMessage from '../components/invalidLinkMessage'

const ResetPassword = () => {
  const {token} = useParams()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTokenVerified , setIsTokenVerified] = useState(false)
  const [status, setStatus] = useState<number | null>(null);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')
    setMessage('')

    if (!password || !confirmPassword) {
      setError('All fields are required.')
      setIsSubmitting(false)
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.')
      setIsSubmitting(false)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      setIsSubmitting(false)
      return
    }

    try {
      const {data} = await axios.post(API_ENDPOINTS.RESET_PASSWORD(), {
        token,
        newPassword: password,
      })
      setMessage(data.message || 'Password updated successfully!')
      setError('')
      setPassword('')
      setConfirmPassword('')
      setTimeout(() => navigate('/login'), 2000)
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'An error occurred. Please try again later.'
      setError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateToken = useCallback(async () => {
    setIsTokenVerified(false)
    setError('')
    setMessage('')

    try {
      const {status} = await axios.post(API_ENDPOINTS.VERIFY_RESET_TOKEN(), {
        token,
      })
      setError('')
      setIsTokenVerified(true)
      setStatus(status)
    } catch (err: any) {
      const statuscode = err?.response?.status
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'An error occurred. Please try again later.'
      setError(msg)
      setStatus(statuscode)
      setIsTokenVerified(false)
    } finally {
    }
  }, [token])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
      validateToken()
    },[validateToken]
  )
  return (
    <>
      <MetaTags title='Reset Password' />
      <div className='reset-password__container'>
        <div className='reset-password__card'>
          <div className='reset-password__header'>
            <h1 className='reset-password__title'>Reset Password</h1>
            {isTokenVerified && 
            <p className='reset-password__subtitle'>Enter your new password below</p>
            }
          </div>

          <form onSubmit={handleSubmit} className='reset-password__form'>
            {status=== 400 ? (
              <InvalidLinkMessage />
            ) : (
              error && (
                <div className="reset-password__error-alert">
                  <span className="reset-password__alert-icon">⚠️</span>
                  {error}
                </div>
              )
              )}

            {message && (
              <div className='reset-password__success-alert'>
                <span className='reset-password__alert-icon'>✅</span>
                {message}
              </div>
            )}
            {isTokenVerified &&
              <>
            <div className='reset-password__input-group'>
              <label className='reset-password__label'>
                <FiLock className='reset-password__input-icon' /> New Password
              </label>
              <div className='reset-password__password-input'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='reset-password__input'
                  disabled={isSubmitting}
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='reset-password__password-toggle'
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            <div className='reset-password__input-group'>
              <label className='reset-password__label'>
                <FiLock className='reset-password__input-icon' /> Confirm Password
              </label>
              <input
                type='password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className='reset-password__input'
                disabled={isSubmitting}
              />
            </div>

            <button
              type='submit'
              className='reset-password__submit-button bg-primary'
              disabled={isSubmitting}
            >
              {isSubmitting ? <span className='reset-password__spinner'></span> : 'Reset Password'}
            </button>
              </>
            }
          </form>

          <Link to='/login' className='reset-password__back-button fs-6 '>
            <FaArrowLeft /> Back to Login
          </Link>
        </div>
      </div>
    </>
  )
}

export default ResetPassword
